import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";

// Helper functions
const formatDate = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleDateString();
};

const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

export const getLeaveRequestStatusColor = (status: string) => {
	switch (status) {
		case "APPROVED":
			return "#4CAF50";
		case "CANCELLED":
			return "#808080";
		case "DECLINED":
			return "#F44336";
		case "PENDING":
			return "#FFC107";
		default:
			return "#2196F3";
	}
};

export function getDaysBetweenDates(startDate: string, endDate: string) {
	const start = new Date(startDate);
	const end = new Date(endDate);
	const diffInMilliseconds = end.getTime() - start.getTime();
	const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
	return diffInDays;
}

// ==================== LEAVE REQUESTS COLUMNS ====================
export const leaveRequestsColumns: ColumnDef<any>[] = [
	{
		accessorKey: "startDate",
		header: "Start Date",
		size: 190,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "returnDate",
		header: "End Date",
		size: 190,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "status",
		header: "Status",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => {
			const status = getValue() as string;
			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getLeaveRequestStatusColor(status) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getLeaveRequestStatusColor(status),
							opacity: 0.15,
						}}
					/>
					<p>{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "comment",
		header: "Comment",
		size: 350,
		minSize: 250,
		maxSize: 600,
	},
	{
		accessorKey: "leavePackage",
		header: "Leave Type",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ row }) => {
			const leaveType = row.original.leavePackage?.leaveType;
			if (!leaveType) return "--";

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: leaveType.colorCode }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: leaveType.colorCode,
							opacity: 0.15,
						}}
					/>
					<p>{leaveType.name}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// ==================== SUPERVISEES LEAVE REQUEST COLUMNS ====================
export const superviseesLeaveRequestColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		minSize: 200,
		maxSize: 400,
		cell: ({ getValue }) => {
			const employee = getValue() as any;
			if (!employee) return "--";
			return (
				<div className="flex items-center space-x-4">
					<img
						src={employee?.photoUrl || "https://via.placeholder.com/40"}
						alt="Employee"
						className="w-10 h-10 rounded-full object-cover"
					/>
					<p className="truncate">
						{(employee?.firstName || "") + " " + (employee?.lastName || "")}
					</p>
				</div>
			);
		},
	},
	...leaveRequestsColumns,
];

// ==================== LEAVE PLANS COLUMNS ====================
export const leavePlansColumns: ColumnDef<any>[] = [
	{
		accessorKey: "intendedStartDate",
		header: "Intended Start Date",
		size: 230,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "intendedReturnDate",
		header: "Intended End Date",
		size: 230,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "comment",
		header: "Comment",
		size: 350,
		minSize: 250,
		maxSize: 600,
	},
	{
		accessorKey: "leavePackage",
		header: "Leave Type",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ row }) => {
			const leaveType = row.original.leavePackage?.leaveType;
			if (!leaveType) return "--";

			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: leaveType.colorCode }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: leaveType.colorCode,
							opacity: 0.15,
						}}
					/>
					<p>{leaveType.name}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		accessorKey: "modifiedAt",
		header: "Date Modified",
		size: 200,
		minSize: 150,
		maxSize: 250,
		cell: ({ getValue }) => formatDate({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// ==================== SUPERVISEES LEAVE PLAN COLUMNS ====================
export const superviseesLeavePlanColumns: ColumnDef<any>[] = [
	{
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		minSize: 200,
		maxSize: 400,
		cell: ({ getValue }) => {
			const employee = getValue() as any;
			if (!employee) return "--";
			return (
				<div className="flex items-center space-x-4">
					<img
						src={employee?.photoUrl || "https://via.placeholder.com/40"}
						alt="Employee"
						className="w-10 h-10 rounded-full object-cover"
					/>
					<p className="truncate">
						{(employee?.firstName || "") + " " + (employee?.lastName || "")}
					</p>
				</div>
			);
		},
	},
	...leavePlansColumns,
];
